// ─── Theory Constraint Engine ─────────────────────────────────────────────────
// Applies domain-specific institutional realism to variable relationships
// BEFORE data generation. Prevents uniformly positive systems by injecting
// contradictions, diminishing returns, and context-specific reversals.

import { getDomainProfile } from './domainRealismProfiles'

// ── Constraint rules by semantic pattern ────────────────────────────────────

const SEMANTIC_CONSTRAINTS = [
  // Expenditure-outcome relationships: often weaker than assumed
  {
    namePattern: /expenditure|spending|budget|invest/i,
    domains: ['public_policy', 'education', 'healthcare', 'sustainability'],
    constraint: (v) => ({
      ...v,
      strength: v.strength === 'strong' ? 'moderate' : v.strength,
      diminishingReturn: true, // flag for narrative use
    }),
  },
  // Governance/compliance: improves reporting, not always outcomes
  {
    namePattern: /governance|compliance|audit|transparency|regulation/i,
    domains: ['public_policy', 'sustainability'],
    constraint: (v) => ({
      ...v,
      // 40% chance governance decouples from operational outcome
      strength: Math.random() < 0.40 ? 'weak' : v.strength,
      decoupled: Math.random() < 0.40,
    }),
  },
  // Training/development: lagged effects, not immediate
  {
    namePattern: /training|development|capacity|education|skill/i,
    domains: ['public_policy', 'human_resources', 'education'],
    constraint: (v) => ({
      ...v,
      laggedEffect: true,
      strength: v.strength === 'strong' ? 'moderate' : v.strength,
    }),
  },
  // Technology: works for high-literacy groups, weaker otherwise
  {
    namePattern: /technology|digital|system|platform|software/i,
    domains: ['technology_adoption', 'public_policy', 'healthcare'],
    constraint: (v) => ({
      ...v,
      moderated: true, // effect is moderated by context
      strength: v.strength === 'strong' ? 'moderate' : v.strength,
    }),
  },
  // Satisfaction: often weak predictor of objective outcomes
  {
    namePattern: /satisfaction|perception|attitude|sentiment/i,
    domains: ['human_resources', 'marketing', 'healthcare'],
    constraint: (v) => ({
      ...v,
      // Attitude-behaviour gap: satisfaction ≠ behaviour
      strength: v.role === 'iv' && Math.random() < 0.35 ? 'weak' : v.strength,
    }),
  },
]

// ── Realism injection rules ────────────────────────────────────────────────────

const REALISM_RULES = {
  // No more than 60% of IVs can have strong expected effects
  maxStrongFraction: 0.60,
  // At least 1 predictor should be weak/none (realistic null finding)
  minWeakCount: 1,
  // Contradiction injection: some IVs get reversed under policy/institutional domains
  contradictionRate: {
    public_policy: 0.25,
    sustainability: 0.20,
    education: 0.18,
    healthcare: 0.15,
    human_resources: 0.15,
    marketing: 0.08,
    finance: 0.08,
    technology_adoption: 0.10,
    operations: 0.10,
  },
}

// ── Main export ────────────────────────────────────────────────────────────────

/**
 * Apply theory-driven constraints to variable definitions before data generation.
 *
 * @param {Variable[]} variables - raw user-defined variables
 * @param {string} domain        - research domain key
 * @param {object} thesis        - thesis metadata
 * @returns {Variable[]}         - constrained variables with realistic adjustments
 */
export function applyTheoryConstraints(variables, domain, thesis) {
  const profile = getDomainProfile(domain)
  const contradictionRate = REALISM_RULES.contradictionRate[domain] ?? 0.15

  let constrained = variables.map(v => {
    let cv = { ...v }

    // Apply semantic constraint rules
    for (const rule of SEMANTIC_CONSTRAINTS) {
      if (rule.namePattern.test(v.name) || rule.namePattern.test(v.label || '')) {
        if (rule.domains.includes(domain)) {
          cv = rule.constraint(cv)
        }
      }
    }

    // Apply domain-level effect size scaling
    if (cv.role === 'iv' || cv.role === 'moderator') {
      const profileStrength = profile.effectSizes[cv.strength] ?? profile.effectSizes.moderate
      // Scale strength down by institutional efficiency
      const scaledStrength = profileStrength * profile.institutionalEfficiency
      if (scaledStrength < 0.20 && cv.strength === 'strong') {
        cv.strength = 'moderate'
      }
    }

    return cv
  })

  // Enforce: at least 1 weak/null predictor among IVs
  const ivs = constrained.filter(v => v.role === 'iv')
  const weakCount = ivs.filter(v => v.strength === 'weak' || v.strength === 'none').length
  if (weakCount < REALISM_RULES.minWeakCount && ivs.length > 1) {
    // Downgrade the last IV with 'moderate' strength
    const candidates = constrained.filter(v => v.role === 'iv' && v.strength === 'moderate')
    if (candidates.length > 0) {
      const target = candidates[candidates.length - 1]
      constrained = constrained.map(v => v.name === target.name ? { ...v, strength: 'weak' } : v)
    }
  }

  // Enforce: cap strong-effect IVs at 60%
  const strongIVs = constrained.filter(v => v.role === 'iv' && v.strength === 'strong')
  const strongFraction = ivs.length > 0 ? strongIVs.length / ivs.length : 0
  if (strongFraction > REALISM_RULES.maxStrongFraction && strongIVs.length > 1) {
    const toDowngrade = strongIVs.slice(Math.ceil(ivs.length * REALISM_RULES.maxStrongFraction))
    const downgradeNames = new Set(toDowngrade.map(v => v.name))
    constrained = constrained.map(v =>
      downgradeNames.has(v.name) ? { ...v, strength: 'moderate' } : v
    )
  }

  // Inject contradictions: flip direction of some weak predictors
  constrained = constrained.map(v => {
    if (v.role !== 'iv') return v
    if (v.strength === 'strong') return v // don't reverse strong effects
    if (Math.random() < contradictionRate) {
      return {
        ...v,
        direction: v.direction === 'positive' ? 'negative' : v.direction === 'negative' ? 'positive' : 'unknown',
        _wasReversed: true, // flag for narrative engine
      }
    }
    return v
  })

  // Add profile metadata for narrative use
  constrained._domainProfile = profile
  constrained._domain = domain
  constrained._appliedConstraints = true

  return constrained
}

/**
 * Generate a human-readable constraints summary for display in Research Lab.
 */
export function describeConstraints(constrainedVariables, domain) {
  const profile = getDomainProfile(domain)
  const ivs = constrainedVariables.filter(v => v.role === 'iv')
  const weakIVs = ivs.filter(v => v.strength === 'weak' || v.strength === 'none')
  const reversedIVs = ivs.filter(v => v._wasReversed)
  const diminishingIVs = ivs.filter(v => v.diminishingReturn)
  const decoupledIVs = ivs.filter(v => v.decoupled)

  const notes = [
    `Domain: ${profile.label} — institutional efficiency index: ${Math.round(profile.institutionalEfficiency * 100)}%`,
    `Expected R² range: ${profile.typicalR2Range[0]}–${profile.typicalR2Range[1]}`,
    `Typical significance rate: ~${Math.round(profile.significanceRate * 100)}% of predictors`,
  ]

  if (weakIVs.length > 0) {
    notes.push(`Null/weak findings injected: ${weakIVs.map(v => v.name).join(', ')}`)
  }
  if (reversedIVs.length > 0) {
    notes.push(`Direction reversed (domain contradiction): ${reversedIVs.map(v => v.name).join(', ')}`)
  }
  if (diminishingIVs.length > 0) {
    notes.push(`Diminishing returns applied: ${diminishingIVs.map(v => v.name).join(', ')}`)
  }
  if (decoupledIVs.length > 0) {
    notes.push(`Institutional decoupling applied: ${decoupledIVs.map(v => v.name).join(', ')} (improves reporting metrics, not outcomes)`)
  }

  return notes
}
